/**
 * Checkbox Component - Reusable checkbox
 *
 * Variants:
 * - default: Standard checkbox
 * - card: Card-style checkbox
 *
 * Sizes:
 * - sm: Small checkbox
 * - md: Medium checkbox (default)
 * - lg: Large checkbox
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

const checkboxVariants = cva(
  'rounded border text-white transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-neutral-300 bg-white focus-visible:ring-primary-500 checked:bg-primary-500 checked:border-primary-500',
        success: 'border-neutral-300 bg-white focus-visible:ring-success-500 checked:bg-success-500 checked:border-success-500',
      },
      size: {
        sm: 'h-4 w-4',
        md: 'h-5 w-5',
        lg: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof checkboxVariants> {
  label?: string;
  description?: string;
  error?: string;
  indeterminate?: boolean;
}

/**
 * Checkbox Component
 *
 * @example
 * ```tsx
 * <Checkbox />
 * <Checkbox label="Accept terms" />
 * <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />
 * <Checkbox indeterminate />
 * ```
 */
const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, variant, size, label, description, error, indeterminate, checked, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Handle indeterminate state
    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate || false;
      }
    }, [indeterminate]);

    return (
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            ref={(node) => {
              // Handle both refs
              if (node) {
                inputRef.current = node;
                if (typeof ref === 'function') {
                  ref(node);
                } else if (ref) {
                  ref.current = node;
                }
              }
            }}
            type="checkbox"
            className={cn(checkboxVariants({ variant, size }), 'relative cursor-pointer')}
            checked={checked}
            aria-invalid={!!error}
            {...props}
          />
          {checked && (
            <Check className={cn(
              'absolute pointer-events-none',
              {
                'h-3 w-3': size === 'sm',
                'h-4 w-4': size === 'md' || !size,
                'h-5 w-5': size === 'lg',
              }
            )} />
          )}
        </div>

        {(label || description) && (
          <div className={cn('ml-2', label ? 'space-y-0.5' : '')}>
            {label && (
              <label
                htmlFor={props.id}
                className={cn(
                  'text-sm font-medium leading-none',
                  error ? 'text-error-500' : 'text-neutral-900',
                  props.disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}
            {description && (
              <p className="text-xs text-neutral-500">
                {description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
